import { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  CircularProgress,
  Avatar,
  Tooltip,
  Link as MuiLink,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Switch,
  Badge,
  LinearProgress,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  AdminPanelSettings as AdminIcon,
  VerifiedUser as VerifiedIcon,
  Person as PersonIcon,
  Star as StarIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  RateReview as RatingIcon,
  SportsFootball as LeagueIcon,
  PersonAdd as NewUserIcon,
  Link as LinkIcon,
  Check as CheckIcon,
  Refresh as RefreshIcon,
  Api as ApiIcon,
  Queue as QueueIcon,
  Tune as TuneIcon,
  Storage as StorageIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  HourglassEmpty as PendingIcon,
  PlayArrow as ProcessingIcon,
  CheckCircle as CompletedIcon,
  Error as ErrorIcon,
  CleaningServices as CleaningServicesIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import {
  adminApi,
  PlatformStats,
  UserWithDetails,
  RatingWithDetails,
  FlaggedUser,
  ActivityItem,
  SleeperApiStats,
  ApiQueueStats,
  SystemStats,
  WorthlessLeague,
  WorthlessLeagueCleanupResult,
} from '../api/admin';
import { getErrorMessage } from '../api/client';

// Supabase free tier database storage ceiling. Used to render a progress
// bar so we can see how close we are to paying for Pro.
const SUPABASE_FREE_DB_LIMIT_BYTES = 500 * 1024 * 1024; // 500 MB

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function StatCard({
  title,
  value,
  icon,
  color = 'primary',
  subtitle,
}: {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error';
  subtitle?: string;
}) {
  return (
    <Card
      sx={{
        height: '100%',
        background: `linear-gradient(135deg, rgba(var(--mui-palette-${color}-mainChannel) / 0.1) 0%, transparent 100%)`,
        border: '1px solid',
        borderColor: `${color}.dark`,
      }}
    >
      {/* Tighter padding (default CardContent is ~16/24px) so all six
          KPI cards fit on one row at md+ without wrapping. */}
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              color="text.secondary"
              variant="caption"
              sx={{
                display: 'block',
                lineHeight: 1.2,
                mb: 0.25,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: `${color}.main`, lineHeight: 1.1 }}
            >
              {typeof value === 'number' ? value.toLocaleString() : value}
            </Typography>
            {subtitle && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: 11, lineHeight: 1.2, display: 'block', mt: 0.25 }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              p: 0.75,
              borderRadius: 1.5,
              bgcolor: `${color}.main`,
              color: `${color}.contrastText`,
              opacity: 0.9,
              display: 'flex',
              flexShrink: 0,
              '& > svg': { fontSize: 18 },
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function AdminPage() {
  const [tab, setTab] = useState(0);
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [users, setUsers] = useState<UserWithDetails[]>([]);
  const [ratings, setRatings] = useState<RatingWithDetails[]>([]);
  const [flaggedUsers, setFlaggedUsers] = useState<FlaggedUser[]>([]);
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [sleeperApiStats, setSleeperApiStats] = useState<SleeperApiStats | null>(null);
  const [apiQueueStats, setApiQueueStats] = useState<ApiQueueStats | null>(null);
  const [systemStats, setSystemStats] = useState<SystemStats | null>(null);
  const [settingSaving, setSettingSaving] = useState<Record<string, boolean>>({});

  // Manual expansion-tick trigger state. Surfaced inline below the
  // Universe expansion row so admins see how the most recent click went.
  const [expansionTickRunning, setExpansionTickRunning] = useState(false);
  const [expansionTickResult, setExpansionTickResult] = useState<{
    severity: 'success' | 'error' | 'warning';
    message: string;
  } | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [usersPage, setUsersPage] = useState(0);
  const [usersRowsPerPage, setUsersRowsPerPage] = useState(10);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersSearch, setUsersSearch] = useState('');

  const [ratingsPage, setRatingsPage] = useState(0);
  const [ratingsRowsPerPage, setRatingsRowsPerPage] = useState(10);
  const [ratingsTotal, setRatingsTotal] = useState(0);
  const [ratingsSearch, setRatingsSearch] = useState('');
  const [showHiddenRatings, setShowHiddenRatings] = useState(true);

  // Dialogs
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    type: 'user' | 'rating';
    id: string;
    name: string;
  }>({ open: false, type: 'user', id: '', name: '' });

  const fetchStats = useCallback(async () => {
    try {
      const data = await adminApi.getStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const data = await adminApi.getUsers({
        page: usersPage + 1,
        limit: usersRowsPerPage,
        search: usersSearch || undefined,
      });
      setUsers(data.users);
      setUsersTotal(data.total);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  }, [usersPage, usersRowsPerPage, usersSearch]);

  const fetchRatings = useCallback(async () => {
    try {
      const data = await adminApi.getRatings({
        page: ratingsPage + 1,
        limit: ratingsRowsPerPage,
        search: ratingsSearch || undefined,
        showHidden: showHiddenRatings,
      });
      setRatings(data.ratings);
      setRatingsTotal(data.total);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  }, [ratingsPage, ratingsRowsPerPage, ratingsSearch, showHiddenRatings]);

  const fetchFlaggedUsers = useCallback(async () => {
    try {
      const data = await adminApi.getFlaggedUsers();
      setFlaggedUsers(data);
    } catch (err) {
      console.error('Failed to fetch flagged users:', err);
    }
  }, []);

  const fetchActivity = useCallback(async () => {
    try {
      const data = await adminApi.getActivity(30);
      setActivity(data);
    } catch (err) {
      console.error('Failed to fetch activity:', err);
    }
  }, []);

  const fetchSleeperApiStats = useCallback(async () => {
    try {
      const data = await adminApi.getSleeperApiStats();
      setSleeperApiStats(data);
    } catch (err) {
      console.error('Failed to fetch Sleeper API stats:', err);
    }
  }, []);

  const fetchApiQueueStats = useCallback(async () => {
    try {
      const data = await adminApi.getApiQueueStats();
      setApiQueueStats(data);
    } catch (err) {
      console.error('Failed to fetch API queue stats:', err);
    }
  }, []);

  const fetchSystemStats = useCallback(async () => {
    try {
      const data = await adminApi.getSystemStats();
      setSystemStats(data);
    } catch (err) {
      console.error('Failed to fetch system stats:', err);
    }
  }, []);

  // Toggle a pause flag. Optimistically updates local state, sends to the
  // server, and refetches stats when done so the `enabled` field is
  // authoritative.
  const handleToggleSetting = useCallback(
    async (key: 'trickle_sync_enabled' | 'expansion_sync_enabled', value: boolean) => {
      setSettingSaving((prev) => ({ ...prev, [key]: true }));
      try {
        await adminApi.updateSystemSetting(key, value);
        await fetchSystemStats();
      } catch (err) {
        console.error(`Failed to update setting ${key}:`, err);
        setError(getErrorMessage(err));
      } finally {
        setSettingSaving((prev) => ({ ...prev, [key]: false }));
      }
    },
    [fetchSystemStats]
  );

  // Manually trigger one expansion tick. Long-poll: the request blocks
  // until the tick completes, which keeps Cloud Run's CPU allocated
  // throughout — that's the whole reason this button exists (it
  // sidesteps request-based CPU throttling that stalls the background
  // setTimeout). Can take 1–2 minutes for a 100-account batch.
  const handleRunExpansionTick = useCallback(async () => {
    setExpansionTickRunning(true);
    setExpansionTickResult(null);
    try {
      const result = await adminApi.runExpansionTick();
      setExpansionTickResult({
        severity:
          result.success && (!result.stats || result.stats.errors === 0)
            ? 'success'
            : 'warning',
        message: result.message,
      });
      await fetchSystemStats();
    } catch (err) {
      const msg = getErrorMessage(err);
      setExpansionTickResult({
        severity: msg.toLowerCase().includes('already running') ? 'warning' : 'error',
        message: msg,
      });
    } finally {
      setExpansionTickRunning(false);
    }
  }, [fetchSystemStats]);

  // Persist a numeric tunable. Same pattern as `handleToggleSetting` but
  // for the integer-valued settings (interval / batch). Server-side
  // validation will round + clamp; we surface any 4xx as an error.
  const handleSaveNumberSetting = useCallback(
    async (key: string, value: number) => {
      setSettingSaving((prev) => ({ ...prev, [key]: true }));
      try {
        await adminApi.updateSystemSetting(key, value);
        await fetchSystemStats();
      } catch (err) {
        console.error(`Failed to update setting ${key}:`, err);
        setError(getErrorMessage(err));
      } finally {
        setSettingSaving((prev) => ({ ...prev, [key]: false }));
      }
    },
    [fetchSystemStats]
  );

  // Persist a string-valued (enum) tunable. Mirrors the number variant
  // above; server validates against the descriptor's allowed list.
  const handleSaveEnumSetting = useCallback(
    async (key: string, value: string) => {
      setSettingSaving((prev) => ({ ...prev, [key]: true }));
      try {
        await adminApi.updateSystemSetting(key, value);
        await fetchSystemStats();
      } catch (err) {
        console.error(`Failed to update setting ${key}:`, err);
        setError(getErrorMessage(err));
      } finally {
        setSettingSaving((prev) => ({ ...prev, [key]: false }));
      }
    },
    [fetchSystemStats]
  );

  // ── Worthless league cleanup ─────────────────────────────────────────
  // Lazily previews the candidates (we don't want to run this aggregation
  // on every page load — only when the admin opens the System tab and
  // hits "Preview").
  const [worthlessLeagues, setWorthlessLeagues] = useState<WorthlessLeague[] | null>(null);
  const [worthlessLoading, setWorthlessLoading] = useState(false);
  const [worthlessConfirmOpen, setWorthlessConfirmOpen] = useState(false);
  const [worthlessRunning, setWorthlessRunning] = useState(false);
  const [worthlessResult, setWorthlessResult] = useState<WorthlessLeagueCleanupResult | null>(null);

  const fetchWorthlessLeagues = useCallback(async () => {
    setWorthlessLoading(true);
    try {
      const data = await adminApi.getWorthlessLeagues();
      setWorthlessLeagues(data.leagues);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setWorthlessLoading(false);
    }
  }, []);

  const handleRunCleanup = useCallback(async () => {
    setWorthlessRunning(true);
    try {
      const result = await adminApi.cleanupWorthlessLeagues();
      setWorthlessResult(result);
      // Re-fetch the candidates so the preview reflects what's left
      // (which should be just the rating-protected ones).
      await fetchWorthlessLeagues();
      // Also refresh stats so Database Size / table breakdown moves.
      await fetchSystemStats();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setWorthlessRunning(false);
      setWorthlessConfirmOpen(false);
    }
  }, [fetchWorthlessLeagues, fetchSystemStats]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchStats(),
        fetchActivity(),
        fetchFlaggedUsers(),
        fetchSleeperApiStats(),
        fetchApiQueueStats(),
        fetchSystemStats(),
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchStats, fetchActivity, fetchFlaggedUsers, fetchSleeperApiStats, fetchApiQueueStats, fetchSystemStats]);

  // Auto-refresh Sleeper API stats and queue stats every 5 seconds (more frequent for queue)
  useEffect(() => {
    const interval = setInterval(() => {
      fetchSleeperApiStats();
      fetchApiQueueStats();
    }, 5000);
    return () => clearInterval(interval);
  }, [fetchSleeperApiStats, fetchApiQueueStats]);

  useEffect(() => {
    if (tab === 1) fetchUsers();
  }, [tab, fetchUsers]);

  useEffect(() => {
    if (tab === 2) fetchRatings();
  }, [tab, fetchRatings]);

  const handleToggleAdmin = async (userId: string, currentStatus: boolean) => {
    try {
      await adminApi.toggleUserAdmin(userId, !currentStatus);
      fetchUsers();
      fetchStats();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleToggleVerified = async (userId: string, currentStatus: boolean) => {
    try {
      await adminApi.toggleUserVerified(userId, !currentStatus);
      fetchUsers();
      fetchStats();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleToggleRatingVisibility = async (ratingId: string, currentVisibility: boolean) => {
    try {
      await adminApi.toggleRatingVisibility(ratingId, !currentVisibility);
      fetchRatings();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleDelete = async () => {
    try {
      if (deleteDialog.type === 'user') {
        await adminApi.deleteUser(deleteDialog.id);
        fetchUsers();
        fetchStats();
      } else {
        await adminApi.deleteRating(deleteDialog.id);
        fetchRatings();
        fetchStats();
      }
      setDeleteDialog({ open: false, type: 'user', id: '', name: '' });
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const getActivityIcon = (type: ActivityItem['type']) => {
    switch (type) {
      case 'user_registered':
        return <NewUserIcon color="success" />;
      case 'rating_created':
        return <StarIcon color="warning" />;
      case 'account_linked':
        return <LinkIcon color="primary" />;
      case 'account_verified':
        return <CheckIcon color="success" />;
      default:
        return <PersonIcon />;
    }
  };

  const getActivityText = (item: ActivityItem) => {
    switch (item.type) {
      case 'user_registered':
        return `New user registered: ${item.details.displayName || item.details.email}`;
      case 'rating_created':
        return `${item.details.raterName} rated ${item.details.ratedUsername}`;
      case 'account_linked':
        return `Sleeper account linked: ${item.details.username}`;
      case 'account_verified':
        return `Account verified: ${item.details.username}`;
      default:
        return 'Unknown activity';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AdminIcon sx={{ fontSize: 40, color: 'error.main' }} />
          <Box>
            <Typography variant="h4" fontWeight={700}>
              Admin Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage users, ratings, and monitor platform activity
            </Typography>
          </Box>
        </Box>
        <Button
          startIcon={<RefreshIcon />}
          onClick={() => {
            fetchStats();
            fetchActivity();
            fetchFlaggedUsers();
            fetchSleeperApiStats();
            fetchApiQueueStats();
            if (tab === 1) fetchUsers();
            if (tab === 2) fetchRatings();
          }}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats Cards — 6 KPIs on one row at md+ (xs={6} 2/row on phones,
          sm={4} 3/row on tablets, md={2} 6/row on desktop). */}
      {stats && (
        <Grid container spacing={1.5} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard
              title="Total Users"
              value={stats.totalUsers}
              icon={<PeopleIcon />}
              color="primary"
              subtitle={`+${stats.newUsersToday} today`}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard
              title="Verified Users"
              value={stats.verifiedUsers}
              icon={<VerifiedIcon />}
              color="success"
              subtitle={`${((stats.verifiedUsers / stats.totalUsers) * 100 || 0).toFixed(1)}%`}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard
              title="Total Ratings"
              value={stats.totalRatings}
              icon={<RatingIcon />}
              color="secondary"
              subtitle={`+${stats.newRatingsToday} today`}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard
              title="Sleeper Accounts"
              value={stats.totalSleeperAccounts}
              icon={<PersonIcon />}
              color="primary"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard
              title="Leagues Tracked"
              value={stats.totalLeagues}
              icon={<LeagueIcon />}
              color="success"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard
              title="Flagged IPs"
              value={stats.flaggedIpUsers}
              icon={<WarningIcon />}
              color={stats.flaggedIpUsers > 0 ? 'warning' : 'success'}
            />
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<TrendingUpIcon />} iconPosition="start" label="Activity" />
          <Tab
            icon={
              <Badge badgeContent={usersTotal} color="primary" max={999}>
                <PeopleIcon />
              </Badge>
            }
            iconPosition="start"
            label="Users"
          />
          <Tab
            icon={
              <Badge badgeContent={ratingsTotal} color="secondary" max={999}>
                <RatingIcon />
              </Badge>
            }
            iconPosition="start"
            label="Ratings"
          />
          <Tab
            icon={
              <Badge badgeContent={flaggedUsers.length} color="warning">
                <WarningIcon />
              </Badge>
            }
            iconPosition="start"
            label="Flagged Users"
          />
          <Tab icon={<TuneIcon />} iconPosition="start" label="System" />
        </Tabs>
      </Paper>

      {/* Activity Tab */}
      <TabPanel value={tab} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Recent Activity
              </Typography>
              <List>
                {activity.map((item, index) => (
                  <ListItem key={index} divider={index < activity.length - 1}>
                    <ListItemIcon>{getActivityIcon(item.type)}</ListItemIcon>
                    <ListItemText
                      primary={getActivityText(item)}
                      secondary={formatDistanceToNow(new Date(item.timestamp), { addSuffix: true })}
                    />
                  </ListItem>
                ))}
                {activity.length === 0 && (
                  <ListItem>
                    <ListItemText primary="No recent activity" />
                  </ListItem>
                )}
              </List>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningIcon color="warning" />
                Shared IP Accounts
              </Typography>
              {flaggedUsers.length > 0 ? (
                <Box>
                  {/* Group users by their shared accounts */}
                  {(() => {
                    // Create groups of users who share IPs
                    const groups: Array<{ users: typeof flaggedUsers }> = [];
                    const processed = new Set<string>();
                    
                    flaggedUsers.forEach(user => {
                      if (processed.has(user.userId)) return;
                      
                      // Find all users in this group (user + their shared users)
                      const groupUserIds = new Set([user.userId, ...user.sharedWithUsers.map(u => u.id)]);
                      const groupUsers = flaggedUsers.filter(u => groupUserIds.has(u.userId));
                      
                      groupUsers.forEach(u => processed.add(u.userId));
                      groups.push({ users: groupUsers });
                    });

                    return groups.slice(0, 5).map((group, groupIndex) => (
                      <Box 
                        key={groupIndex} 
                        sx={{ 
                          mb: 2, 
                          p: 1.5, 
                          bgcolor: 'warning.dark', 
                          borderRadius: 1,
                          opacity: 0.9,
                        }}
                      >
                        <Typography variant="caption" sx={{ color: 'warning.contrastText', fontWeight: 600 }}>
                          Shared IP Group {groupIndex + 1}
                        </Typography>
                        <List dense sx={{ py: 0 }}>
                          {group.users.map(user => (
                            <ListItem key={user.userId} sx={{ py: 0.25, px: 0 }}>
                              <ListItemIcon sx={{ minWidth: 28 }}>
                                <PersonIcon sx={{ fontSize: 16, color: 'warning.contrastText' }} />
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Typography variant="body2" sx={{ color: 'warning.contrastText', fontWeight: 500 }}>
                                    {user.displayName}
                                  </Typography>
                                }
                                secondary={
                                  <Typography variant="caption" sx={{ color: 'warning.contrastText', opacity: 0.8 }}>
                                    {user.email}
                                  </Typography>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    ));
                  })()}
                  {flaggedUsers.length > 10 && (
                    <Typography variant="caption" color="text.secondary">
                      ... and more groups
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography color="text.secondary">No shared IP accounts detected</Typography>
              )}
            </Paper>

            {/* Sleeper API Stats */}
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ApiIcon color="primary" />
                Sleeper API Usage
              </Typography>
              {sleeperApiStats ? (
                <Box>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2">
                        Current Minute: {sleeperApiStats.currentMinuteRequests} / {sleeperApiStats.rateLimitPerMinute}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {sleeperApiStats.currentUsagePercent.toFixed(1)}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(sleeperApiStats.currentUsagePercent, 100)}
                      color={
                        sleeperApiStats.currentUsagePercent > 80
                          ? 'error'
                          : sleeperApiStats.currentUsagePercent > 50
                          ? 'warning'
                          : 'success'
                      }
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                  <Divider sx={{ my: 1.5 }} />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Today's Requests:</Typography>
                      <Typography variant="body2" fontWeight={600}>{sleeperApiStats.totalRequestsToday.toLocaleString()}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">All-Time Requests:</Typography>
                      <Typography variant="body2" fontWeight={600}>{sleeperApiStats.totalRequestsAllTime.toLocaleString()}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Peak (per min):</Typography>
                      <Typography variant="body2" fontWeight={600}>{sleeperApiStats.peakRequestsPerMinute}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Rate Limit Hits:</Typography>
                      <Typography variant="body2" fontWeight={600} color={sleeperApiStats.rateLimitHits > 0 ? 'error.main' : 'text.primary'}>
                        {sleeperApiStats.rateLimitHits}
                      </Typography>
                    </Box>
                  </Box>
                  {sleeperApiStats.lastRequestTime && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Last request: {formatDistanceToNow(new Date(sleeperApiStats.lastRequestTime), { addSuffix: true })}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography color="text.secondary">Loading...</Typography>
              )}
            </Paper>

            {/* API Queue Status */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <QueueIcon color="secondary" />
                API Queue Status
                {apiQueueStats && apiQueueStats.pending > 0 && (
                  <Chip 
                    label={`${apiQueueStats.pending + apiQueueStats.processing} pending`}
                    size="small"
                    color="warning"
                    sx={{ ml: 1 }}
                  />
                )}
              </Typography>
              {apiQueueStats ? (
                <Box>
                  {/* Queue Summary */}
                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={3}>
                      <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'warning.dark', borderRadius: 1, opacity: 0.9 }}>
                        <PendingIcon sx={{ fontSize: 20, color: 'warning.contrastText' }} />
                        <Typography variant="h6" sx={{ color: 'warning.contrastText', fontWeight: 700 }}>
                          {apiQueueStats.pending}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'warning.contrastText' }}>Pending</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={3}>
                      <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'info.dark', borderRadius: 1, opacity: 0.9 }}>
                        <ProcessingIcon sx={{ fontSize: 20, color: 'info.contrastText' }} />
                        <Typography variant="h6" sx={{ color: 'info.contrastText', fontWeight: 700 }}>
                          {apiQueueStats.processing}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'info.contrastText' }}>Processing</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={3}>
                      <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'success.dark', borderRadius: 1, opacity: 0.9 }}>
                        <CompletedIcon sx={{ fontSize: 20, color: 'success.contrastText' }} />
                        <Typography variant="h6" sx={{ color: 'success.contrastText', fontWeight: 700 }}>
                          {apiQueueStats.totalProcessed}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'success.contrastText' }}>Completed</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={3}>
                      <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'error.dark', borderRadius: 1, opacity: 0.9 }}>
                        <ErrorIcon sx={{ fontSize: 20, color: 'error.contrastText' }} />
                        <Typography variant="h6" sx={{ color: 'error.contrastText', fontWeight: 700 }}>
                          {apiQueueStats.failed}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'error.contrastText' }}>Failed</Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 1.5 }} />

                  {/* Queue Stats */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Avg Processing Time:</Typography>
                      <Typography variant="body2" fontWeight={600}>{apiQueueStats.averageProcessingTime}ms</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Requests/Min:</Typography>
                      <Typography variant="body2" fontWeight={600}>{apiQueueStats.requestsPerMinute}</Typography>
                    </Box>
                    {apiQueueStats.pending > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Est. Time to Empty:</Typography>
                        <Typography variant="body2" fontWeight={600}>~{apiQueueStats.estimatedTimeToEmpty}s</Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Queue Status:</Typography>
                      <Chip 
                        label={apiQueueStats.isProcessing ? 'Running' : 'Stopped'}
                        size="small"
                        color={apiQueueStats.isProcessing ? 'success' : 'error'}
                      />
                    </Box>
                  </Box>

                  {/* Queued Requests List */}
                  {apiQueueStats.queuedRequests.length > 0 && (
                    <>
                      <Divider sx={{ my: 1.5 }} />
                      <Typography variant="subtitle2" gutterBottom>
                        Outstanding Requests ({apiQueueStats.queuedRequests.length})
                      </Typography>
                      <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                        <List dense sx={{ py: 0 }}>
                          {apiQueueStats.queuedRequests.map((req) => (
                            <ListItem key={req.id} sx={{ py: 0.25, px: 0 }}>
                              <ListItemIcon sx={{ minWidth: 28 }}>
                                {req.status === 'processing' ? (
                                  <CircularProgress size={14} />
                                ) : (
                                  <PendingIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                )}
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                    {req.type}: {req.endpoint}
                                  </Typography>
                                }
                                secondary={
                                  <Typography variant="caption" color="text.secondary">
                                    Priority: {req.priority} • {formatDistanceToNow(new Date(req.createdAt), { addSuffix: true })}
                                  </Typography>
                                }
                              />
                              <Chip 
                                label={req.status}
                                size="small"
                                color={req.status === 'processing' ? 'info' : 'default'}
                                sx={{ fontSize: '0.65rem', height: 20 }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    </>
                  )}

                  {apiQueueStats.lastProcessedAt && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Last processed: {formatDistanceToNow(new Date(apiQueueStats.lastProcessedAt), { addSuffix: true })}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography color="text.secondary">Loading...</Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Users Tab */}
      <TabPanel value={tab} index={1}>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ mb: 2 }}>
            <TextField
              size="small"
              placeholder="Search users..."
              value={usersSearch}
              onChange={(e) => {
                setUsersSearch(e.target.value);
                setUsersPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 300 }}
            />
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Sleeper Username</TableCell>
                  <TableCell>Last Login</TableCell>
                  <TableCell>Last IP</TableCell>
                  <TableCell align="center">Ratings</TableCell>
                  <TableCell align="center">Verified</TableCell>
                  <TableCell align="center">Admin</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar src={user.avatarUrl || undefined} sx={{ width: 32, height: 32 }}>
                          {user.displayName?.[0] || '?'}
                        </Avatar>
                        <Typography variant="body2">{user.displayName || 'Unknown'}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      {user.sleeperUsername ? (
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {user.sleeperUsername}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {user.lastLoginAt ? (
                        <Typography variant="body2">
                          {new Date(user.lastLoginAt).toLocaleDateString()}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Never
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {user.lastIpAddress || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {user.ratingsGiven} / {user.ratingsReceived}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        size="small"
                        checked={user.isVerified}
                        onChange={() => handleToggleVerified(user.id, user.isVerified)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        size="small"
                        checked={user.isAdmin}
                        onChange={() => handleToggleAdmin(user.id, user.isAdmin)}
                        color="error"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Delete User">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() =>
                            setDeleteDialog({
                              open: true,
                              type: 'user',
                              id: user.id,
                              name: user.displayName,
                            })
                          }
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={usersTotal}
            page={usersPage}
            onPageChange={(_e, page) => setUsersPage(page)}
            rowsPerPage={usersRowsPerPage}
            onRowsPerPageChange={(e) => {
              setUsersRowsPerPage(parseInt(e.target.value));
              setUsersPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </Paper>
      </TabPanel>

      {/* Ratings Tab */}
      <TabPanel value={tab} index={2}>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search ratings..."
              value={ratingsSearch}
              onChange={(e) => {
                setRatingsSearch(e.target.value);
                setRatingsPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 300 }}
            />
            <Chip
              label={showHiddenRatings ? 'Showing Hidden' : 'Hiding Hidden'}
              color={showHiddenRatings ? 'warning' : 'default'}
              onClick={() => setShowHiddenRatings(!showHiddenRatings)}
              icon={showHiddenRatings ? <VisibilityIcon /> : <VisibilityOffIcon />}
            />
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Rater</TableCell>
                  <TableCell>Rated User</TableCell>
                  <TableCell>Review</TableCell>
                  <TableCell align="center">Avg Score</TableCell>
                  <TableCell align="center">Visible</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ratings.map((rating) => {
                  const avgScore = (
                    (rating.payment_reliability +
                      rating.communication +
                      rating.fair_play +
                      rating.league_commitment +
                      rating.fair_trader) /
                    5
                  ).toFixed(1);

                  return (
                    <TableRow
                      key={rating.id}
                      hover
                      sx={{ opacity: rating.is_visible === false ? 0.5 : 1 }}
                    >
                      <TableCell>
                        <Typography variant="body2">{rating.raterDisplayName}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {rating.raterEmail}
                        </Typography>
                      </TableCell>
                      <TableCell>{rating.ratedUsername}</TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>
                        <Typography variant="body2" noWrap title={rating.review_text || ''}>
                          {rating.review_text || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={avgScore}
                          size="small"
                          color={
                            parseFloat(avgScore) >= 4
                              ? 'success'
                              : parseFloat(avgScore) >= 3
                              ? 'warning'
                              : 'error'
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleToggleRatingVisibility(rating.id, rating.is_visible !== false)}
                          color={rating.is_visible !== false ? 'success' : 'default'}
                        >
                          {rating.is_visible !== false ? (
                            <VisibilityIcon fontSize="small" />
                          ) : (
                            <VisibilityOffIcon fontSize="small" />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {formatDistanceToNow(new Date(rating.created_at), { addSuffix: true })}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Delete Rating">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() =>
                              setDeleteDialog({
                                open: true,
                                type: 'rating',
                                id: rating.id,
                                name: `rating from ${rating.raterDisplayName}`,
                              })
                            }
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={ratingsTotal}
            page={ratingsPage}
            onPageChange={(_e, page) => setRatingsPage(page)}
            rowsPerPage={ratingsRowsPerPage}
            onRowsPerPageChange={(e) => {
              setRatingsRowsPerPage(parseInt(e.target.value));
              setRatingsPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </Paper>
      </TabPanel>

      {/* Flagged Users Tab */}
      <TabPanel value={tab} index={3}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" />
            Users with Shared IP Addresses
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            These users have logged in from the same IP address, which may indicate shared accounts or
            potential manipulation.
          </Typography>

          {flaggedUsers.length === 0 ? (
            <Alert severity="success">No flagged users detected</Alert>
          ) : (
            <Grid container spacing={2}>
              {flaggedUsers.map((user) => (
                <Grid item xs={12} md={6} key={user.userId}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Avatar>{user.displayName[0]}</Avatar>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {user.displayName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {user.email}
                          </Typography>
                        </Box>
                      </Box>
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        Shares {user.sharedIpCount} IP address(es) with other users
                      </Alert>
                      <Typography variant="body2" fontWeight={600} gutterBottom>
                        Shared with:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {user.sharedWithUsers.map((shared) => (
                          <Chip
                            key={shared.id}
                            label={shared.displayName}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
      </TabPanel>

      {/* System Tab — pause switches, DB size, background-sync status */}
      <TabPanel value={tab} index={4}>
        <Grid container spacing={3}>
          {/* Background sync jobs */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <TuneIcon color="primary" />
                  <Typography variant="h6">Background Data Jobs</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  These two jobs gradually keep the DB fresh and grow the
                  universe of known users. Pause either here — changes take
                  effect within ~30 seconds.
                </Typography>

                {systemStats ? (
                  <>
                    {/* Trickle sync row */}
                    <SyncJobRow
                      label="Trickle refresh"
                      description="Every hour, re-pulls the 5 stalest accounts and 3 stalest leagues so our data tracks changes on Sleeper."
                      enabled={systemStats.trickleSync.enabled}
                      isRunning={systemStats.trickleSync.isRunning}
                      lastRunAt={systemStats.trickleSync.lastRunAt}
                      detail={
                        systemStats.trickleSync.lastRunStats
                          ? `Last tick: ${systemStats.trickleSync.lastRunStats.accountsRefreshed} accounts, ${systemStats.trickleSync.lastRunStats.leaguesRefreshed} leagues, ${systemStats.trickleSync.lastRunStats.errors} errors`
                          : 'No ticks run yet'
                      }
                      saving={!!settingSaving.trickle_sync_enabled}
                      onToggle={(v) =>
                        handleToggleSetting('trickle_sync_enabled', v)
                      }
                    />
                    <Divider sx={{ my: 2 }} />
                    {/* Expansion sync row — interval and batch are
                        admin-tunable and read fresh on each tick, so
                        edits take effect within one cycle. */}
                    <SyncJobRow
                      label="Universe expansion"
                      description={
                        `Every ${systemStats.expansionSync.intervalMinutes} min, picks ${systemStats.expansionSync.batchSize} shadow accounts and syncs their ` +
                        (systemStats.expansionSync.seasons === 'all'
                          ? 'leagues across current + 3 prior seasons (best for growing the DB).'
                          : 'current-season leagues only (cheaper, but plateaus on new-account discovery in saturated networks).') +
                        ' On request-throttled Cloud Run the background timer can stall — use "Run now" to fire a tick on demand.'
                      }
                      enabled={systemStats.expansionSync.enabled}
                      isRunning={systemStats.expansionSync.isRunning}
                      lastRunAt={systemStats.expansionSync.lastRunAt}
                      detail={
                        `${systemStats.expansionSync.pendingAccounts.toLocaleString()} accounts waiting to be enriched` +
                        (systemStats.expansionSync.lastRunStats
                          ? ` · last tick: ${systemStats.expansionSync.lastRunStats.accountsEnriched} enriched, ${systemStats.expansionSync.lastRunStats.errors} errors`
                          : '')
                      }
                      saving={!!settingSaving.expansion_sync_enabled}
                      onToggle={(v) =>
                        handleToggleSetting('expansion_sync_enabled', v)
                      }
                      onRunNow={handleRunExpansionTick}
                      runningNow={expansionTickRunning}
                      runNowLabel="Run tick now"
                      runNowResult={expansionTickResult}
                      tunables={[
                        {
                          key: 'expansion_sync_interval_minutes',
                          label: 'Tick every',
                          unit: 'min',
                          value: systemStats.expansionSync.intervalMinutes,
                          min: systemStats.expansionSync.bounds.minIntervalMinutes,
                          max: systemStats.expansionSync.bounds.maxIntervalMinutes,
                          saving: !!settingSaving.expansion_sync_interval_minutes,
                          onSave: (v) =>
                            handleSaveNumberSetting(
                              'expansion_sync_interval_minutes',
                              v
                            ),
                        },
                        {
                          key: 'expansion_sync_batch_size',
                          label: 'Accounts/tick',
                          unit: '',
                          value: systemStats.expansionSync.batchSize,
                          min: systemStats.expansionSync.bounds.minBatchSize,
                          max: systemStats.expansionSync.bounds.maxBatchSize,
                          saving: !!settingSaving.expansion_sync_batch_size,
                          onSave: (v) =>
                            handleSaveNumberSetting(
                              'expansion_sync_batch_size',
                              v
                            ),
                        },
                      ]}
                      enumTunables={[
                        {
                          key: 'expansion_sync_seasons',
                          label: 'Season scope',
                          value: systemStats.expansionSync.seasons,
                          options: [
                            { value: 'all', label: 'All (current + 3 prior) — finds new accounts' },
                            { value: 'current', label: 'Current only — cheap, plateaus' },
                          ],
                          saving: !!settingSaving.expansion_sync_seasons,
                          onSave: (v) =>
                            handleSaveEnumSetting('expansion_sync_seasons', v),
                        },
                      ]}
                    />
                  </>
                ) : (
                  <CircularProgress size={24} />
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Database size */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <StorageIcon color="primary" />
                  <Typography variant="h6">Database Size</Typography>
                </Box>
                {systemStats ? (
                  <>
                    <Typography
                      variant="h4"
                      sx={{ fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 800, mb: 1 }}
                    >
                      {systemStats.database.totalPretty}
                    </Typography>
                    {(() => {
                      const bytes = Number(systemStats.database.totalBytes);
                      const pct = Math.min(100, (bytes / SUPABASE_FREE_DB_LIMIT_BYTES) * 100);
                      return (
                        <>
                          <LinearProgress
                            variant="determinate"
                            value={pct}
                            color={pct > 80 ? 'error' : pct > 50 ? 'warning' : 'primary'}
                            sx={{ height: 8, borderRadius: 1, mb: 1 }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {pct.toFixed(1)}% of 500 MB Supabase free tier
                          </Typography>
                        </>
                      );
                    })()}

                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Top tables by size
                    </Typography>
                    <List dense>
                      {systemStats.database.tables.slice(0, 10).map((t) => (
                        <ListItem key={t.name} sx={{ py: 0.25 }}>
                          <ListItemText
                            primary={t.name}
                            secondary={t.pretty}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </>
                ) : (
                  <CircularProgress size={24} />
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Worthless league cleanup */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <CleaningServicesIcon color="primary" />
                  <Typography variant="h6">Worthless League Cleanup</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  New syncs already refuse to store these. Use this tool to purge
                  ones that snuck in before the filter existed:
                </Typography>
                <List dense sx={{ mb: 2, pl: 2 }}>
                  <ListItem sx={{ display: 'list-item', listStyleType: 'disc', py: 0 }}>
                    <ListItemText primary="Past-season leagues that never reached complete status (no winner)." />
                  </ListItem>
                  <ListItem sx={{ display: 'list-item', listStyleType: 'disc', py: 0 }}>
                    <ListItemText primary="Leagues configured for fewer than 2 teams (Sleeper total_rosters < 2) — true solo test leagues." />
                  </ListItem>
                </List>
                <Alert severity="info" sx={{ mb: 2 }}>
                  We use the league's <b>configured size</b> (Sleeper's
                  total_rosters), not the count of members in our DB. Our
                  membership table can be incomplete for real leagues if a sync
                  was interrupted, so deleting based on it would risk false
                  positives. Leagues with attached ratings (manager or
                  commissioner) are also always skipped.
                </Alert>

                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={
                      worthlessLoading ? <CircularProgress size={16} /> : <RefreshIcon />
                    }
                    onClick={fetchWorthlessLeagues}
                    disabled={worthlessLoading}
                  >
                    {worthlessLeagues === null ? 'Preview Candidates' : 'Refresh Preview'}
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setWorthlessConfirmOpen(true)}
                    disabled={
                      !worthlessLeagues ||
                      worthlessLeagues.filter(
                        (l) => l.ratingCount === 0 && l.commissionerRatingCount === 0
                      ).length === 0
                    }
                  >
                    Delete Worthless Leagues
                  </Button>
                </Box>

                {worthlessLeagues !== null && (
                  <>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {worthlessLeagues.length === 0
                        ? 'No worthless leagues found — nothing to clean up.'
                        : `${worthlessLeagues.length} candidate(s) found · ${worthlessLeagues.filter((l) => l.ratingCount === 0 && l.commissionerRatingCount === 0).length} would be deleted`}
                    </Typography>
                    {worthlessLeagues.length > 0 && (
                      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
                        <Table stickyHeader size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Name</TableCell>
                              <TableCell>Season</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Reason</TableCell>
                              <TableCell align="right">
                                <Tooltip title="Sleeper's configured league size (total_rosters)">
                                  <span>Size</span>
                                </Tooltip>
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip title="Membership rows we have in our DB (may be incomplete after a partial sync)">
                                  <span>In DB</span>
                                </Tooltip>
                              </TableCell>
                              <TableCell align="right">Ratings</TableCell>
                              <TableCell>Action</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {worthlessLeagues.map((l) => {
                              const totalRatings = l.ratingCount + l.commissionerRatingCount;
                              const willDelete = totalRatings === 0;
                              return (
                                <TableRow key={l.id} hover>
                                  <TableCell>
                                    {/* Open in a new tab so the admin's
                                        place in the cleanup queue is
                                        preserved while they inspect. */}
                                    <MuiLink
                                      component={RouterLink}
                                      to={`/league/${l.id}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      underline="hover"
                                      sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        color: 'primary.main',
                                        fontWeight: 500,
                                      }}
                                    >
                                      {l.name}
                                      <OpenInNewIcon
                                        sx={{
                                          fontSize: 14,
                                          opacity: 0.7,
                                        }}
                                      />
                                    </MuiLink>
                                  </TableCell>
                                  <TableCell>{l.season}</TableCell>
                                  <TableCell>
                                    <Chip label={l.status} size="small" variant="outlined" />
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={
                                        l.reason === 'solo_league_config'
                                          ? 'Solo (config)'
                                          : l.reason === 'solo_league_membership'
                                            ? 'Solo (membership)'
                                            : 'Past season unfinished'
                                      }
                                      size="small"
                                      color="warning"
                                      variant="outlined"
                                    />
                                  </TableCell>
                                  <TableCell align="right">{l.totalRosters}</TableCell>
                                  <TableCell align="right">{l.membershipCount}</TableCell>
                                  <TableCell align="right">{totalRatings}</TableCell>
                                  <TableCell>
                                    {willDelete ? (
                                      l.reason === 'solo_league_membership' ? (
                                        <Tooltip title="Re-syncs from Sleeper first; deletes only if Sleeper still shows <2 users (otherwise restores missing memberships).">
                                          <Chip
                                            label="Will verify"
                                            size="small"
                                            color="warning"
                                          />
                                        </Tooltip>
                                      ) : (
                                        <Chip
                                          label="Will delete"
                                          size="small"
                                          color="error"
                                        />
                                      )
                                    ) : (
                                      <Tooltip title="Skipped: has attached ratings">
                                        <Chip
                                          label="Will keep"
                                          size="small"
                                          color="success"
                                          variant="outlined"
                                        />
                                      </Tooltip>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </>
                )}

                {worthlessResult && (
                  <Alert
                    severity={worthlessResult.deleted > 0 ? 'success' : 'info'}
                    sx={{ mt: 2 }}
                    onClose={() => setWorthlessResult(null)}
                  >
                    Cleanup finished: deleted {worthlessResult.deleted} of{' '}
                    {worthlessResult.candidatesFound} candidate(s).
                    {worthlessResult.skippedDueToRatings > 0 && (
                      <> {worthlessResult.skippedDueToRatings} skipped due to attached ratings.</>
                    )}
                    {worthlessResult.membershipResyncRestored > 0 && (
                      <> {worthlessResult.membershipResyncRestored} restored after re-sync (memberships filled in).</>
                    )}
                    {worthlessResult.membershipResyncErrors > 0 && (
                      <> {worthlessResult.membershipResyncErrors} re-sync error(s) — see server logs.</>
                    )}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ ...deleteDialog, open: false })}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {deleteDialog.name}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ ...deleteDialog, open: false })}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Worthless league cleanup confirmation */}
      <Dialog
        open={worthlessConfirmOpen}
        onClose={() => !worthlessRunning && setWorthlessConfirmOpen(false)}
      >
        <DialogTitle>Delete Worthless Leagues</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete{' '}
            {worthlessLeagues
              ? worthlessLeagues.filter(
                  (l) => l.ratingCount === 0 && l.commissionerRatingCount === 0
                ).length
              : 0}{' '}
            league(s) and their league memberships. Leagues with attached ratings
            will be skipped automatically. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setWorthlessConfirmOpen(false)}
            disabled={worthlessRunning}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRunCleanup}
            color="error"
            variant="contained"
            disabled={worthlessRunning}
            startIcon={
              worthlessRunning ? <CircularProgress size={16} /> : <DeleteIcon />
            }
          >
            {worthlessRunning ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Helper: single row for one background sync job ──────────────────────

interface NumericTunable {
  key: string;
  label: string;
  unit: string;
  value: number;
  min: number;
  max: number;
  saving: boolean;
  onSave: (newValue: number) => void;
}

interface EnumTunable {
  key: string;
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  saving: boolean;
  onSave: (newValue: string) => void;
}

interface SyncJobRowProps {
  label: string;
  description: string;
  enabled: boolean;
  isRunning: boolean;
  lastRunAt: string | null;
  detail: string;
  saving: boolean;
  onToggle: (newValue: boolean) => void;
  // Optional list of admin-tunable numeric settings rendered below the
  // status line as "label [input] unit [Save]" rows.
  tunables?: NumericTunable[];
  // Optional enum-valued settings rendered as "label [select]" rows
  // below the numeric tunables.
  enumTunables?: EnumTunable[];
  // Optional "Run now" action — renders a button next to the status
  // chip that calls `onRunNow` and shows a spinner while `runningNow`
  // is true. `runNowResult` is displayed inline as an alert after the
  // run completes (or fails).
  onRunNow?: () => void;
  runningNow?: boolean;
  runNowLabel?: string;
  runNowResult?: { severity: 'success' | 'error' | 'warning'; message: string } | null;
}

function SyncJobRow({
  label,
  description,
  enabled,
  isRunning,
  lastRunAt,
  detail,
  saving,
  onToggle,
  tunables,
  enumTunables,
  onRunNow,
  runningNow,
  runNowLabel,
  runNowResult,
}: SyncJobRowProps) {
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {enabled ? <PlayIcon color="success" /> : <PauseIcon color="warning" />}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {description}
          </Typography>
        </Box>
        {saving ? (
          <CircularProgress size={22} />
        ) : (
          <Switch
            checked={enabled}
            onChange={(_e, v) => onToggle(v)}
            color="primary"
          />
        )}
      </Box>
      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Chip
          size="small"
          label={isRunning || runningNow ? 'Running now' : enabled ? 'Scheduled' : 'Paused'}
          color={isRunning || runningNow ? 'info' : enabled ? 'success' : 'default'}
          variant={enabled ? 'filled' : 'outlined'}
        />
        <Typography variant="caption" color="text.secondary">
          {lastRunAt
            ? `Last ran ${formatDistanceToNow(new Date(lastRunAt), { addSuffix: true })}`
            : 'Never run yet'}
        </Typography>
        {onRunNow && (
          <Button
            size="small"
            variant="outlined"
            onClick={onRunNow}
            disabled={runningNow}
            startIcon={
              runningNow ? <CircularProgress size={14} /> : <PlayIcon fontSize="small" />
            }
            sx={{ ml: 'auto' }}
          >
            {runningNow ? 'Running…' : runNowLabel ?? 'Run now'}
          </Button>
        )}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        {detail}
      </Typography>
      {runNowResult && (
        <Alert severity={runNowResult.severity} sx={{ mt: 1 }} variant="outlined">
          {runNowResult.message}
        </Alert>
      )}
      {tunables && tunables.length > 0 && (
        <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {tunables.map(({ key, ...rest }) => (
            <TunableField key={key} {...rest} />
          ))}
        </Box>
      )}
      {enumTunables && enumTunables.length > 0 && (
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {enumTunables.map(({ key, ...rest }) => (
            <EnumTunableField key={key} {...rest} />
          ))}
        </Box>
      )}
    </Box>
  );
}

// Inline select for a single string-valued setting. Saves on change
// (no separate Save button — these are short enums, the round-trip is
// fast enough that explicit confirmation would be friction).
function EnumTunableField({
  label,
  value,
  options,
  saving,
  onSave,
}: Omit<EnumTunable, 'key'>) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 110 }}>
        {label}
      </Typography>
      <TextField
        select
        size="small"
        value={value}
        disabled={saving}
        onChange={(e) => {
          if (e.target.value !== value) onSave(e.target.value);
        }}
        SelectProps={{ native: true }}
        sx={{ minWidth: 280 }}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </TextField>
      {saving && <CircularProgress size={16} />}
    </Box>
  );
}

// Inline editor for a single numeric setting. Tracks a draft value so
// the admin can type freely without firing a write per keystroke; Save
// is enabled only when the draft differs from the current saved value
// and is in range.
function TunableField({
  label,
  unit,
  value,
  min,
  max,
  saving,
  onSave,
}: Omit<NumericTunable, 'key'>) {
  const [draft, setDraft] = useState<string>(String(value));

  // Sync draft when the source-of-truth changes (e.g. after a save
  // that round-trips through fetchSystemStats, or another admin edits
  // it in another tab).
  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const parsed = parseInt(draft, 10);
  const isValid = Number.isFinite(parsed) && parsed >= min && parsed <= max;
  const isDirty = isValid && parsed !== value;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 100 }}>
        {label}
      </Typography>
      <TextField
        size="small"
        type="number"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        inputProps={{ min, max, step: 1, style: { width: 70 } }}
        error={!isValid && draft.trim() !== ''}
        helperText={!isValid && draft.trim() !== '' ? `${min}–${max}` : undefined}
        FormHelperTextProps={{ sx: { m: 0, fontSize: 10 } }}
        disabled={saving}
      />
      <Typography variant="caption" color="text.secondary">
        {unit}
      </Typography>
      <Button
        size="small"
        variant="outlined"
        onClick={() => onSave(parsed)}
        disabled={!isDirty || saving}
        sx={{ minWidth: 60 }}
      >
        {saving ? <CircularProgress size={14} /> : 'Save'}
      </Button>
      <Typography variant="caption" color="text.secondary">
        ({min}–{max})
      </Typography>
    </Box>
  );
}

